import { useTranslation } from 'react-i18next';

import { App } from 'antd';
import FileAccess, { FileLimitation } from 'interfaces/FileAccess';
import {
  CreateFileFromTemplatePayload,
  CreateFolderPayload,
  DeleteFileAccessPayload,
  GetFilesPayload,
  RevokeDenyFileAccessPayload,
  UpdateFileAccessPayload,
} from 'interfaces/FileService';
import Group from 'interfaces/Group';
import User from 'interfaces/User';
import { useCallback, useEffect, useRef } from 'react';
import { FileFromTemplateForm } from '../components/Forms/FileFromTemplateForm';
import { FileMoveForm } from '../components/Forms/FileMoveForm';
import { FileShareForm } from '../components/Forms/FileShareForm';
import { FileShortcutForm } from '../components/Forms/FileShortcutForm';
import { FileTitleForm } from '../components/Forms/FileTitleForm';
import { EventBusEventEnum } from '../enums/EventBusEventEnum';
import File from '../interfaces/File';
import EventBus from '../services/EventBus';
import { FileService } from '../services/fileService';
import { useAuth } from './useAuth';
import { useFileUpload } from './useFileUpload';
import { useModal } from './useModal';
import { useTags } from './useTags';
import useOpenFile from './useOpenFile';
import FileSellForm from '../components/Forms/FileSellForm';
interface Props {
  folderUid?: string;
}

export interface UseFilesActionsEvents {
  onFilesCreated?: (newFiles: File[]) => void;
  onFilesUpdated?: (updatedFiles: File[]) => void;
  onFilesDeleted?: (deletedFiles: File[]) => void;
}

interface UseFilesActionsReturn {
  handleCreateFolder: (events?: UseFilesActionsEvents) => void;
  handleCreateFileFromTemplate: (
    template: 'word' | 'excel' | 'powerpoint',
    events?: UseFilesActionsEvents
  ) => void;
  handleDownloadEncryptedFile: (file: File) => void;
  handleRenameFile: (file: File, events?: UseFilesActionsEvents) => void;
  handleFavouriteFileChange: (
    files: File[],
    isFavourite: boolean,
    events?: UseFilesActionsEvents
  ) => void;
  handleCreateFileShortcut: (
    file: File,
    events?: UseFilesActionsEvents
  ) => void;
  handleCreateFileDuplicate: (
    file: File,
    events?: UseFilesActionsEvents
  ) => void;
  handleReplaceFile: (file: File, events?: UseFilesActionsEvents) => void;
  handleMoveFiles: (
    files: File[],
    parentFolder?: File | null,
    externalFilters?: Partial<GetFilesPayload>,
    events?: UseFilesActionsEvents
  ) => void;
  handleDeleteFiles: (files: File[], events?: UseFilesActionsEvents) => void;
  handleRestoreFilesFromTrash: (
    files: File[],
    events?: UseFilesActionsEvents
  ) => void;
  handlePermanentlyDeleteFiles: (
    files: File[],
    events?: UseFilesActionsEvents
  ) => void;
  handleHideFiles: (files: File[], events?: UseFilesActionsEvents) => void;
  handleUnhideFiles: (files: File[], events?: UseFilesActionsEvents) => void;
  handleShareFiles: (files: File[], events?: UseFilesActionsEvents) => void;
  handleUpdateTags: (
    file: File,
    tagIds: number[],
    events?: UseFilesActionsEvents
  ) => void;
  handleOpenFileFromTrash: (file: File, events?: UseFilesActionsEvents) => void;
  handleOpenShortcutOriginalFileLocation: (
    file: File,
    events?: UseFilesActionsEvents
  ) => void;
  handleSellFile: (file: File, events?: UseFilesActionsEvents) => void;
}

const fileService = new FileService();

export function useFilesActions({ folderUid }: Props): UseFilesActionsReturn {
  const { t } = useTranslation();
  const modal = useModal();
  const folderUidRef = useRef(folderUid);

  const { onBehalfOfUser, isAdminOrContributor, authenticatedUser } = useAuth();
  const { notification } = App.useApp();
  const { uploadFiles } = useFileUpload();
  const { tags } = useTags();
  const { redirectFileToLocation } = useOpenFile();

  /**
   * @desc Create a new folder
   * @param title - The title of the folder
   */
  const createFolder = useCallback(
    (title: string, events?: UseFilesActionsEvents) => {
      EventBus.emit(EventBusEventEnum.IsGlobalLoaderVisible, {
        isVisible: true,
      });

      const payload: CreateFolderPayload = {
        name: title,
        parent: folderUidRef.current || undefined,
        onBehalfOf: onBehalfOfUser?.userId || undefined,
      };

      fileService
        .createFolder(payload)
        .then((response) => {
          const newFolder: File = {
            ...response,
            isUnRead: true,
          };
          events?.onFilesCreated?.([newFolder]);
        })
        .catch((error) => {
          console.error(error);
          notification.error({
            message: t('errors.genericCreateError', {
              resource: t('common.folder'),
            }),
          });
        })
        .finally(() => {
          EventBus.emit(EventBusEventEnum.IsGlobalLoaderVisible, {
            isVisible: false,
          });
        });
    },
    [folderUidRef.current, onBehalfOfUser, t, notification]
  );

  /**
   * @desc Create a new file from a template
   * @param title - The title of the file
   * @param template - The template to use
   */
  const createFileFromTemplate = useCallback(
    (
      title: string,
      template: 'word' | 'excel' | 'powerpoint',
      events?: UseFilesActionsEvents
    ) => {
      EventBus.emit(EventBusEventEnum.IsGlobalLoaderVisible, {
        isVisible: true,
      });

      const payload: CreateFileFromTemplatePayload = {
        name: title,
        parent: folderUidRef.current
          ? parseInt(folderUidRef.current)
          : undefined,
        template: template,
        onBehalfOf: onBehalfOfUser?.userId || undefined,
      };

      fileService
        .createFileFromTemplate(payload)
        .then((response) => {
          const newFile: File = {
            ...response,
            isUnRead: true,
          };
          events?.onFilesCreated?.([newFile]);
        })
        .catch((error) => {
          notification.error({
            message: t('errors.genericCreateError', {
              resource: t('common.file'),
            }),
          });
        })
        .finally(() => {
          EventBus.emit(EventBusEventEnum.IsGlobalLoaderVisible, {
            isVisible: false,
          });
        });
    },
    [folderUidRef.current, onBehalfOfUser, t, notification]
  );

  /**
   * @desc Rename a file
   * @param file - The file to rename
   * @param newName - The new name of the file
   */
  const renameFile = useCallback(
    (file: File, newName: string, events?: UseFilesActionsEvents) => {
      EventBus.emit(EventBusEventEnum.IsGlobalLoaderVisible, {
        isVisible: true,
      });
      fileService
        .renameFile(file.fileId, newName)
        .then(() => {
          const updatedFile: File = {
            ...file,
            title: newName,
          };
          events?.onFilesUpdated?.([updatedFile]);
        })
        .catch((error) => {
          console.error(error);
          notification.error({
            message: t('errors.genericUpdateError', {
              resource: t('common.file'),
            }),
          });
        })
        .finally(() => {
          EventBus.emit(EventBusEventEnum.IsGlobalLoaderVisible, {
            isVisible: false,
          });
        });
    },
    [fileService, t, notification]
  );

  /**
   * @desc Create a new file duplicate
   * @param file - The file to create the duplicate of
   */
  const createFileDuplicate = useCallback(
    (file: File, events?: UseFilesActionsEvents) => {
      EventBus.emit(EventBusEventEnum.IsGlobalLoaderVisible, {
        isVisible: true,
      });
      fileService
        .duplicateFiles([file.fileId], t('common.copyOf'))
        .then((response) => {
          const newFile: File = {
            ...response.myFiles[0],
            isUnRead: true,
          };
          events?.onFilesCreated?.([newFile]);
        })
        .catch(() => {
          notification.error({
            message: t('errors.genericCreateError', {
              resource: t('common.file'),
            }),
          });
        })
        .finally(() => {
          EventBus.emit(EventBusEventEnum.IsGlobalLoaderVisible, {
            isVisible: false,
          });
        });
    },
    [fileService, t, notification]
  );

  /**
   * @desc Change the favourite status of a file
   * @param file - The file to change the favourite status of
   * @param isFavourite - Whether the file is favourite
   */
  const changeFavouriteFile = useCallback(
    (files: File[], isFavourite: boolean, events?: UseFilesActionsEvents) => {
      EventBus.emit(EventBusEventEnum.IsGlobalLoaderVisible, {
        isVisible: true,
      });
      fileService
        .updateFavouriteFiles(
          files.map((f) => f.fileId),
          isFavourite
        )
        .then(() => {
          const updatedFiles: File[] = files.map((f) => ({
            ...f,
            isFavourite: isFavourite,
          }));
          events?.onFilesUpdated?.(updatedFiles);
        })
        .catch(() => {
          notification.error({
            message: t('errors.genericUpdateError', {
              resource: t('common.file'),
            }),
          });
        })
        .finally(() => {
          EventBus.emit(EventBusEventEnum.IsGlobalLoaderVisible, {
            isVisible: false,
          });
        });
    },
    [fileService, t, notification]
  );

  /**
   * @desc Create a new file shortcut
   * @param file - The file to create the shortcut of
   */
  const createFileShortcut = useCallback(
    (file: File, folderUid: number | null, events?: UseFilesActionsEvents) => {
      EventBus.emit(EventBusEventEnum.IsGlobalLoaderVisible, {
        isVisible: true,
      });
      fileService
        .createShortcut(file.fileId, folderUid?.toString() || undefined)
        .then((response) => {
          const newFile: File = {
            ...response,
            isUnRead: true,
          };
          events?.onFilesCreated?.([newFile]);
        })
        .catch(() => {
          notification.error({
            message: t('errors.genericUpdateError', {
              resource: t('common.file'),
            }),
          });
        })
        .finally(() => {
          EventBus.emit(EventBusEventEnum.IsGlobalLoaderVisible, {
            isVisible: false,
          });
        });
    },
    [fileService, t, notification]
  );

  /**
   * @desc Replace a file
   * @param file - The file to replace
   */
  const replaceFile = useCallback(
    (file: File, events?: UseFilesActionsEvents) => {
      const input = document.createElement('input');
      input.type = 'file';
      input.accept = file.fileExtension;
      input.onchange = () => {
        const selectedFiles = input.files;
        if (selectedFiles) {
          uploadFiles(
            selectedFiles,
            false,
            folderUidRef.current ? parseInt(folderUidRef?.current) : undefined,
            file.fileId
          );
        }
      };
      input.click();
    },
    [folderUidRef.current, uploadFiles]
  );

  /**
   * @desc Move files
   * @param folder - The folder to move the files to
   * @param filesToMove - The files to move
   */
  const moveFiles = useCallback(
    (
      folderUid: number | null,
      filesToMove: File[],
      events?: UseFilesActionsEvents
    ) => {
      if (filesToMove?.length === 0) {
        return;
      }

      const fileIds = filesToMove.map((f) => f?.fileId);

      if (fileIds.length === 0) {
        return;
      }

      EventBus.emit(EventBusEventEnum.IsGlobalLoaderVisible, {
        isVisible: true,
      });

      fileService
        .moveFiles(folderUid, fileIds)
        .then(() => {
          events?.onFilesDeleted?.(filesToMove);
          events?.onFilesUpdated?.(filesToMove);
        })
        .catch(() => {
          notification.error({
            message: t('errors.errorMoveFile'),
          });
        })
        .finally(() => {
          EventBus.emit(EventBusEventEnum.IsGlobalLoaderVisible, {
            isVisible: false,
          });
        });
    },
    [fileService, t, notification]
  );

  /**
   * @desc Delete files
   * @param files - The files to delete
   */
  const deleteFiles = useCallback(
    (files: File[], events?: UseFilesActionsEvents) => {
      EventBus.emit(EventBusEventEnum.IsGlobalLoaderVisible, {
        isVisible: true,
      });

      fileService
        .updateDeletedFiles(
          files.map((f) => f.fileId),
          true
        )
        .then(() => {
          events?.onFilesDeleted?.(files);
        })
        .catch(() => {
          notification.error({
            message: t('errors.errorDeleteFiles'),
          });
        })
        .finally(() => {
          EventBus.emit(EventBusEventEnum.IsGlobalLoaderVisible, {
            isVisible: false,
          });
        });
    },
    [fileService, t, notification]
  );

  /**
   * @desc Delete files
   * @param files - The files to delete
   */
  const restoreFilesFromTrash = useCallback(
    (files: File[], events?: UseFilesActionsEvents) => {
      EventBus.emit(EventBusEventEnum.IsGlobalLoaderVisible, {
        isVisible: true,
      });

      fileService
        .updateDeletedFiles(
          files.map((f) => f.fileId),
          false
        )
        .then(() => {
          events?.onFilesDeleted?.(files);
        })
        .catch(() => {
          notification.error({
            message: t('errors.errorRestoreFiles'),
          });
        })
        .finally(() => {
          EventBus.emit(EventBusEventEnum.IsGlobalLoaderVisible, {
            isVisible: false,
          });
        });
    },
    [fileService, t, notification]
  );

  /**
   * @desc Permanently delete files
   * @param files - The files to permanently delete
   */
  const permanentlyDeleteFiles = useCallback(
    (files: File[], events?: UseFilesActionsEvents) => {
      EventBus.emit(EventBusEventEnum.IsGlobalLoaderVisible, {
        isVisible: true,
      });

      fileService
        .deleteFiles(files.map((f) => f.fileId))
        .then(() => {
          events?.onFilesDeleted?.(files);
        })
        .catch(() => {
          notification.error({
            message: t('errors.errorPermanentlyDeleteFiles'),
          });
        })
        .finally(() => {
          EventBus.emit(EventBusEventEnum.IsGlobalLoaderVisible, {
            isVisible: false,
          });
        });
    },
    [fileService, t, notification]
  );

  /**
   * @desc Hide files
   * @param files - The files to hide
   */
  const hideFiles = useCallback(
    (files: File[], events?: UseFilesActionsEvents) => {
      EventBus.emit(EventBusEventEnum.IsGlobalLoaderVisible, {
        isVisible: true,
      });

      fileService
        .updateArchivedFiles(
          files.map((f) => f.fileId),
          true
        )
        .then(() => {
          events?.onFilesDeleted?.(files);
        })
        .catch(() => {
          notification.error({
            message: t('errors.errorHideFiles'),
          });
        })
        .finally(() => {
          EventBus.emit(EventBusEventEnum.IsGlobalLoaderVisible, {
            isVisible: false,
          });
        });
    },
    [fileService, t, notification]
  );

  /**
   * @desc Unhide files
   * @param files - The files to unhide
   */
  const unhideFiles = useCallback(
    (files: File[], events?: UseFilesActionsEvents) => {
      EventBus.emit(EventBusEventEnum.IsGlobalLoaderVisible, {
        isVisible: true,
      });

      fileService
        .updateArchivedFiles(
          files.map((f) => f.fileId),
          false
        )
        .then(() => {
          events?.onFilesDeleted?.(files);
        })
        .catch(() => {
          notification.error({
            message: t('errors.errorUnhideFiles'),
          });
        })
        .finally(() => {
          EventBus.emit(EventBusEventEnum.IsGlobalLoaderVisible, {
            isVisible: false,
          });
        });
    },
    [fileService, t, notification]
  );

  /**
   * @desc Share files
   * @param files - The files to share
   * @param newUsers - The new users to share the files to
   * @param newGroups - The new groups to share the files to
   * @param fileAccessesToRemove - The file accesses to remove
   * @param fileAccessesToUpdate - The file accesses to update
   * @param sendMessage - Whether to send a message
   * @param message - The message to send
   * @param readOnly - Whether the files are read only
   * @param expiry - The expiry date
   */
  const shareFiles = useCallback(
    (
      files: File[],
      newUsers: User[],
      newGroups: Group[],
      fileAccessesToRemove: FileAccess[],
      fileAccessesToUpdate: FileAccess[],
      fileAccessesRevokeDenied: FileAccess[],
      sendMessage: boolean,
      message: string,
      readOnly: boolean,
      expiry: number,
      events?: UseFilesActionsEvents
    ) => {
      const payloads: UpdateFileAccessPayload[] = [];
      if (
        newUsers.length === 0 &&
        newGroups.length === 0 &&
        fileAccessesToUpdate.length > 0
      ) {
        fileAccessesToUpdate.forEach((fileAccess) => {
          const isUser = !!fileAccess.user;

          const item: UpdateFileAccessPayload = {
            recipients: [
              isUser
                ? fileAccess.user?.email || ''
                : fileAccess.group?.name || '',
            ],
            allRecipients: [
              {
                recipient: isUser
                  ? fileAccess.user?.email || ''
                  : fileAccess.group?.name || '',
                isGroup: !isUser,
              },
            ],
            files: files.map((f) => f.fileId),
            onBehalfOf: onBehalfOfUser?.userId,
            limitations: {
              antiCapture: fileAccess.fileLimitation.antiCapture,
              expiry: fileAccess.fileLimitation.accessExpiration,
              readOnly: !fileAccess.fileLimitation.canEdit,
            },
          };
          payloads.push(item);
        });
      } else if (newUsers.length > 0 || newGroups.length > 0) {
        // Create and send a request for sharing files with these recipients, applying specified limitations.
        const payload: UpdateFileAccessPayload = {
          recipients: [
            ...newUsers.map((u) => u.email),
            ...newGroups.map((g) => g.name),
          ],
          // Create an array of object with users and groups
          allRecipients: [
            ...newUsers.map((u) => ({
              recipient: u.email,
              isGroup: false,
            })),
            ...newGroups.map((g) => ({
              recipient: g.name,
              isGroup: true,
            })),
          ],
          files: files.map((f) => f.fileId),
          limitations: {
            antiCapture: false,
            expiry: expiry,
            readOnly: readOnly,
          },
          // uniqueCopy: u.uniqueCopy,
        };
        // Add message if showMessageBox is true and messageForNewShare is not empty
        if (sendMessage && message.trim() !== '') {
          payload.message = message;
        } else if (sendMessage) {
          payload.message = '';
        } else {
          payload.message = null;
        }
        payloads.push(payload);
      }

      EventBus.emit(EventBusEventEnum.IsGlobalLoaderVisible, {
        isVisible: true,
      });

      if (fileAccessesRevokeDenied.length > 0) {
        fileAccessesRevokeDenied.forEach((fileAccess) => {
          const payload: RevokeDenyFileAccessPayload = {
            fileIds: files.map((f) => f.uid),
            recipients: fileAccessesRevokeDenied.map((f) =>
              !!f.user
                ? {
                    id: f.user.userId || 0,
                    isGroup: false,
                  }
                : {
                    id: f.group?.id || 0,
                    isGroup: true,
                  }
            ),
            onBehalfOf: onBehalfOfUser?.userId,
          };

          fileService
            .revokeDeniedFileAccess(payload)
            .then(() => {})
            .catch(() => {
              notification.error({
                message: t('errors.shareFiles'),
              });
            })
            .finally(() => {
              EventBus.emit(EventBusEventEnum.IsGlobalLoaderVisible, {
                isVisible: false,
              });
            });
        });
      }

      payloads.forEach((payload) => {
        fileService
          .updateFileAccess(payload)
          .then(() => {
            // Update each file by setting the isShared property to true
            events?.onFilesUpdated?.(
              files.map((f) => ({
                ...f,
                isShared: true,
              }))
            );
          })
          .catch(() => {
            notification.error({
              message: t('errors.shareFiles'),
            });
          })
          .finally(() => {
            EventBus.emit(EventBusEventEnum.IsGlobalLoaderVisible, {
              isVisible: false,
            });
          });
      });

      if (fileAccessesToRemove.length > 0) {
        // Build the payload for the request
        const payload: DeleteFileAccessPayload = {
          fileIds: files.map((f) => f.uid),
          recipients: fileAccessesToRemove.map((f) =>
            !!f.user
              ? {
                  id: f.user.userId || 0,
                  isGroup: false,
                }
              : {
                  id: f.group?.id || 0,
                  isGroup: true,
                }
          ),
        };

        EventBus.emit(EventBusEventEnum.IsGlobalLoaderVisible, {
          isVisible: true,
        });

        fileService
          .deleteFileAccess(payload)
          .then((response) => {
            // Calculate for each file the number of file accesses
            events?.onFilesUpdated?.(
              files.map((f) => ({
                ...f,
                isShared: false,
              }))
            );
          })
          .catch(() => {
            notification.error({
              message: t('errors.errorRemoveShareFiles'),
            });
          })
          .finally(() => {
            EventBus.emit(EventBusEventEnum.IsGlobalLoaderVisible, {
              isVisible: false,
            });
          });
      }
    },
    [modal, t]
  );

  /**
   * @desc Update the tags of a file
   * @param file - The file to update the tags of
   * @param tagIds - The tag IDs to update the file with
   */
  const updateTags = useCallback(
    (file: File, tagIds: number[], events?: UseFilesActionsEvents) => {
      EventBus.emit(EventBusEventEnum.IsGlobalLoaderVisible, {
        isVisible: true,
      });

      fileService
        .updateFileTags(file.uid, tagIds)
        .then(() => {
          // Find the tags and update the array
          const foundTags = tags.filter((t) => tagIds.includes(t.id));

          const updatedFile = {
            ...file,
            tags: foundTags.map((t) => t.id),
            tagsV2: foundTags.map((t) => ({
              id: t.id,
              readOnly: false,
            })),
          };

          events?.onFilesUpdated?.([updatedFile]);
        })
        .catch(() => {
          notification.error({
            message: t('errors.genericUpdateError', {
              resource: t('common.tags'),
            }),
          });
        })
        .finally(() => {
          EventBus.emit(EventBusEventEnum.IsGlobalLoaderVisible, {
            isVisible: false,
          });
        });
    },
    [fileService, t, notification, tags]
  );

  /**
   * @desc Open the original file location of a shortcut
   * @param file - The file to open the original file location of
   */
  const openShortcutOriginalFileLocation = useCallback(
    (file: File, events?: UseFilesActionsEvents) => {
      EventBus.emit(EventBusEventEnum.IsGlobalLoaderVisible, {
        isVisible: true,
      });

      fileService
        .getFile(file.uid)
        .then(async (fileDetail) => {
          const updatedFile = {
            ...file,
            parentId: fileDetail.originalShortcutParentId,
          };

          redirectFileToLocation(updatedFile, true);
        })
        .catch((error) => {
          console.error(error);
          notification.error({
            message: t('errors.genericFetchError', {
              resource: t('common.file'),
            }),
          });
        })
        .finally(() => {
          EventBus.emit(EventBusEventEnum.IsGlobalLoaderVisible, {
            isVisible: false,
          });
        });
    },
    [fileService, t, notification]
  );

  /**
   * @desc Open the modal to create a new folder
   */
  const handleCreateFolder = useCallback(
    (events?: UseFilesActionsEvents) => {
      modal
        .open({
          title: t('modal.fileTitleForm.newFolderTitle'),
          content: (
            <FileTitleForm
              isFolder={true}
              onSubmit={(title) => {
                createFolder(title, events);
                modal.close(true, title);
              }}
              onCancel={() => {
                modal.close(true, null);
              }}
            />
          ),
          confirmLabel: t('common.create'),
        })
        .then(() => {});
    },
    [modal, t, notification]
  );

  /**
   * @desc Open the modal to create a new file from a template
   * @param template - The template to use
   */
  const handleCreateFileFromTemplate = useCallback(
    (
      template: 'word' | 'excel' | 'powerpoint',
      events?: UseFilesActionsEvents
    ) => {
      if (!isAdminOrContributor) {
        notification.error({
          message: t('errors.noUploadPermission'),
        });
        return;
      }

      let title = '';

      switch (template) {
        case 'word':
          title = t('modal.fileFromTemplateForm.newWordDocumentTitle');
          break;
        case 'excel':
          title = t('modal.fileFromTemplateForm.newExcelDocumentTitle');
          break;
        case 'powerpoint':
          title = t('modal.fileFromTemplateForm.newPowerpointDocumentTitle');
          break;
        default:
          title = t('modal.fileFromTemplateForm.newGenericDocumentTitle');
      }

      modal
        .open({
          title,
          content: (
            <FileFromTemplateForm
              template={template}
              onSubmit={(title) => {
                createFileFromTemplate(title, template, events);
                modal.close(true, title);
              }}
              onCancel={() => {
                modal.close(true, null);
              }}
            />
          ),
          confirmLabel: t('common.create'),
        })
        .then(() => {});
    },
    [modal, t, notification]
  );

  /**
   * @desc Download the encrypted file
   * @param file - The file to download
   * @param free - Whether the file is free
   */
  const handleDownloadEncryptedFile = async (file: File, free = 0) => {
    EventBus.emit(EventBusEventEnum.IsGlobalLoaderVisible, {
      isVisible: true,
    });

    fileService
      .downloadPdf(file.fileId, free)
      .then(async (response) => {
        const blob = await response.blob();
        if (blob) {
          const href = URL.createObjectURL(blob);
          const link = document.createElement('a');
          if (free === 0) link.download = file.title + '.pdf';
          else link.download = file.title;
          link.href = href;
          link.click();
          URL.revokeObjectURL(href);
        }
      })
      .catch(() => {
        notification.error({
          message: t('errors.hitASnag'),
        });
      })
      .finally(() => {
        EventBus.emit(EventBusEventEnum.IsGlobalLoaderVisible, {
          isVisible: false,
        });
      });
  };

  /**
   * @desc Open the modal to rename a file
   * @param file - The file to rename
   */
  const handleRenameFile = useCallback(
    (file: File, events?: UseFilesActionsEvents) => {
      modal
        .open({
          title: file.isFolder
            ? t('modal.fileTitleForm.editFolderTitle')
            : t('modal.fileTitleForm.editFileTitle'),
          content: (
            <FileTitleForm
              file={file}
              onSubmit={(title) => {
                renameFile(file, title, events);
                modal.close(true, title);
              }}
              onCancel={() => {
                modal.close(true, null);
              }}
            />
          ),
          confirmLabel: t('common.rename'),
        })
        .then(() => {});
    },
    [modal, t, notification]
  );

  /**
   * @desc Change the favourite status of a file
   * @param file - The file to change the favourite status of
   * @param isFavourite - Whether the file is favourite
   */
  const handleFavouriteFileChange = useCallback(
    (files: File[], isFavourite: boolean, events?: UseFilesActionsEvents) => {
      changeFavouriteFile(files, isFavourite, events);
    },
    [changeFavouriteFile]
  );

  /**
   * @desc Create a new file shortcut
   * @param file - The file to create the shortcut of
   */
  const handleCreateFileShortcut = useCallback(
    (file: File, events?: UseFilesActionsEvents) => {
      modal
        .open({
          title: t('modal.fileShortcutForm.title', {
            resource: file?.title,
          }),
          content: (
            <FileShortcutForm
              selectedFile={file}
              currentFolder={null}
              onBehalfOfUser={onBehalfOfUser}
              onSubmit={(folderUid) => {
                createFileShortcut(file, folderUid, events);
                modal.close(true, folderUid);
              }}
              onCancel={() => {
                modal.close(true, null);
              }}
            />
          ),
        })
        .then(() => {});
    },
    [createFileShortcut]
  );

  /**
   * @desc Create a new file duplicate
   * @param file - The file to create the duplicate of
   */
  const handleCreateFileDuplicate = useCallback(
    (file: File, events?: UseFilesActionsEvents) => {
      createFileDuplicate(file, events);
    },
    [createFileDuplicate]
  );

  /**
   * @desc Replace a file
   * @param file - The file to replace
   */
  const handleReplaceFile = useCallback(
    (file: File, events?: UseFilesActionsEvents) => {
      replaceFile(file, events);
    },
    [replaceFile]
  );

  /**
   * @desc Open the modal to move files
   * @param files - The files to move
   * @param parentFolder - The parent folder
   * @param externalFilters - The external filters
   */
  const handleMoveFiles = useCallback(
    (
      files: File[],
      parentFolder?: File | null,
      externalFilters?: Partial<GetFilesPayload>,
      events?: UseFilesActionsEvents
    ) => {
      modal
        .open({
          title: t('modal.fileMoveForm.title', {
            resource: files?.[0]?.title,
            count: files.length,
          }),
          content: (
            <FileMoveForm
              selectedFiles={files}
              currentFolder={parentFolder}
              externalFilters={externalFilters}
              authenticatedUser={authenticatedUser}
              onBehalfOfUser={onBehalfOfUser}
              onSubmit={(folderUid) => {
                moveFiles(folderUid, files, events);
                modal.close(true, folderUid);
              }}
              onCancel={() => {
                modal.close(true, null);
              }}
            />
          ),
        })
        .then(() => {});
    },
    [modal, t, notification]
  );

  /**
   * @desc Delete files
   * @param files - The files to delete
   */
  const handleDeleteFiles = useCallback(
    (files: File[], events?: UseFilesActionsEvents) => {
      deleteFiles(files, events);
    },
    [deleteFiles]
  );

  /**
   * @desc Restore files from trash
   * @param files - The files to restore
   */
  const handleRestoreFilesFromTrash = useCallback(
    (files: File[], events?: UseFilesActionsEvents) => {
      restoreFilesFromTrash(files, events);
    },
    [restoreFilesFromTrash]
  );

  /**
   * @desc Permanently delete files
   * @param files - The files to permanently delete
   */
  const handlePermanentlyDeleteFiles = useCallback(
    (files: File[], events?: UseFilesActionsEvents) => {
      modal
        .open({
          title: t('modal.filePermanentlyDeleteForm.title', {
            resource: files?.[0]?.title,
            count: files.length,
          }),
          message: t('modal.filePermanentlyDeleteForm.description', {
            resource: files?.[0]?.title,
            count: files.length,
          }),
        })
        .then(() => {
          permanentlyDeleteFiles(files, events);
        });
    },
    [permanentlyDeleteFiles]
  );

  /**
   * @desc Hide files
   * @param files - The files to hide
   */
  const handleHideFiles = useCallback(
    (files: File[], events?: UseFilesActionsEvents) => {
      hideFiles(files, events);
    },
    [hideFiles]
  );

  /**
   * @desc Unhide files
   * @param files - The files to unhide
   */
  const handleUnhideFiles = useCallback(
    (files: File[], events?: UseFilesActionsEvents) => {
      unhideFiles(files, events);
    },
    [unhideFiles]
  );

  /**
   * @desc Sell file
   */
  const sellFile = useCallback(
    (file: File, events?: UseFilesActionsEvents) => {
      const updatedFile: File = {
        ...file,
      };

      events?.onFilesUpdated?.([updatedFile]);
    },
    [modal, t]
  );

  /**
   * @desc removeFileFromStore
   */
  const removeFileFromStore = useCallback(
    (file: File, events?: UseFilesActionsEvents) => {
      const updatedFile: File = {
        ...file,
      };

      events?.onFilesUpdated?.([updatedFile]);
    },
    [modal, t]
  );

  /**
   * @desc Open the modal to share files
   * @param files - The files to share
   */
  const handleShareFiles = useCallback(
    (files: File[], events?: UseFilesActionsEvents) => {
      modal
        .open({
          title: t('modal.fileShareForm.title', {
            resource: files?.[0]?.title,
            count: files.length,
          }),
          message: t('modal.fileShareForm.message', {
            resource: files?.[0]?.title,
            count: files.length,
          }),
          content: (
            <FileShareForm
              selectedFiles={files}
              authenticatedUser={authenticatedUser}
              onBehalfOfUser={onBehalfOfUser}
              onSubmit={(
                newUsers,
                newGroups,
                fileAccessesToRemove,
                fileAccessesToUpdate,
                fileAccessesRevokeDenied,
                sendMessage,
                message,
                readOnly,
                expiry
              ) => {
                modal.close(true, null);
                shareFiles(
                  files,
                  newUsers,
                  newGroups,
                  fileAccessesToRemove,
                  fileAccessesToUpdate,
                  fileAccessesRevokeDenied,
                  sendMessage,
                  message,
                  readOnly,
                  expiry,
                  events
                );
              }}
              onCancel={() => {
                modal.close(true, null);
              }}
            />
          ),
        })
        .then(() => {});
    },
    []
  );

  /**
   * @description Update the tags of a file.
   */
  const handleUpdateTags = useCallback(
    (file: File, tagIds: number[], events?: UseFilesActionsEvents) => {
      updateTags(file, tagIds, events);
    },
    [updateTags]
  );

  /**
   * @desc Open a file from trash
   * @param file - The file to open
   */
  const handleOpenFileFromTrash = useCallback(
    (file: File, events?: UseFilesActionsEvents) => {
      modal
        .open({
          title: t('modal.openFileFromTrash.title', {
            resource: file?.title,
          }),
          message: t('modal.openFileFromTrash.description', {
            resource: file?.title,
          }),
        })
        .then(() => {
          restoreFilesFromTrash([file], events);
        });
    },
    [restoreFilesFromTrash]
  );

  /**
   * @desc Open the original file location of a shortcut
   * @param file - The file to open the original file location of
   */
  const handleOpenShortcutOriginalFileLocation = useCallback(
    (file: File, events?: UseFilesActionsEvents) => {
      openShortcutOriginalFileLocation(file, events);
    },
    [openShortcutOriginalFileLocation]
  );

  const handleSellFile = useCallback(
    (file: File, events?: UseFilesActionsEvents) => {
      modal
        .open({
          title: t('modal.fileSellForm.title', {
            resource: file?.title,
          }),
          message: t('modal.fileSellForm.message', {
            resource: file?.title,
          }),
          content: (
            <FileSellForm
              file={file}
              onBehalfOfUser={onBehalfOfUser}
              authenticatedUser={authenticatedUser}
              onSubmit={(file) => {
                sellFile(file, events);
              }}
              onCancel={() => {
                modal.close(true, null);
              }}
              onClose={() => {
                modal.close(true, null);
              }}
              onRemoveFromStore={(file) => {
                modal.close(true, null);
                removeFileFromStore(file);
              }}
            />
          ),
        })
        .then(() => {});
    },
    [fileService, t, notification]
  );

  useEffect(() => {
    folderUidRef.current = folderUid;
  }, [folderUid]);

  return {
    handleCreateFolder,
    handleCreateFileFromTemplate,
    handleDownloadEncryptedFile,
    handleRenameFile,
    handleFavouriteFileChange,
    handleCreateFileShortcut,
    handleCreateFileDuplicate,
    handleReplaceFile,
    handleMoveFiles,
    handleDeleteFiles,
    handleRestoreFilesFromTrash,
    handlePermanentlyDeleteFiles,
    handleHideFiles,
    handleUnhideFiles,
    handleShareFiles,
    handleUpdateTags,
    handleOpenFileFromTrash,
    handleOpenShortcutOriginalFileLocation,
    handleSellFile,
  };
}

export default useFilesActions;
